import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { resetPassword } from '@/services/api/auth';
import { usePaperTheme } from '@/hooks/usePaperTheme';

export function ResetPasswordPage() {
  usePaperTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setConfirmError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setConfirmError('Passwords do not match');
      return;
    }
    setConfirmError('');
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error((err as Error).message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const invalidToken = !token || token.length < 32;

  return (
    <div className="min-h-screen bg-paper text-charcoal-soft">
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Link to="/" className="text-lg font-semibold tracking-tight-ish text-charcoal">
          clyra<span className="text-teal-500">.</span>
        </Link>
        <Link to="/login" className="text-petit text-charcoal-muted hover:text-charcoal transition-colors">
          Sign in
        </Link>
      </nav>

      <main className="mx-auto flex max-w-sm flex-col px-6 pt-12 pb-20">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight-ish text-charcoal">
            Reset your password
          </h1>
          <p className="mt-1 text-petit text-charcoal-muted">
            Choose a new password for your account.
          </p>
        </div>

        <div className="rounded-xl border border-stone-subtle bg-paper-raised p-6 shadow-paper-sm">
          {invalidToken ? (
            <div className="space-y-2 text-center">
              <p className="text-sm text-charcoal">This reset link looks broken.</p>
              <p className="text-petit text-charcoal-muted">
                Request a new one from the{' '}
                <Link to="/forgot-password" className="text-teal-600 hover:text-teal-500">
                  forgot-password page
                </Link>
                .
              </p>
            </div>
          ) : done ? (
            <div className="space-y-2 text-center">
              <p className="text-sm font-medium text-charcoal">Password updated.</p>
              <p className="text-petit text-charcoal-muted">Taking you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordInput
                label="New password"
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                autoFocus
              />
              <PasswordInput
                label="Confirm password"
                placeholder="Repeat your password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                error={confirmError}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <Button type="submit" className="w-full" loading={loading} disabled={!password || !confirm}>
                Set new password
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-petit text-charcoal-muted">
          <Link to="/login" className="text-teal-600 hover:text-teal-500 transition-colors">
            Back to sign in
          </Link>
        </p>
      </main>
    </div>
  );
}
